import type { HelpArticle, HelpCategory } from "@/lib/registrant-help/types";

export const INSPECTION_HELP_ARTICLE_IDS = [
  "getting-started",
  "inspection-queue",
  "perform-inspection",
  "inspection-documents",
] as const;

export type InspectionHelpArticleId =
  (typeof INSPECTION_HELP_ARTICLE_IDS)[number];

export const INSPECTION_HELP_CATEGORIES: HelpCategory[] = [
  {
    id: "getting-started",
    title: "Getting started",
    summary: "Sign in and find your way around the inspection portal.",
  },
  {
    id: "inspections",
    title: "Inspections",
    summary: "Queue, perform inspections, and attach documents.",
  },
];

export const INSPECTION_HELP_ARTICLES: HelpArticle[] = [
  {
    id: "getting-started",
    title: "Getting started",
    summary:
      "A tour of the inspection portal: sign-in, the queue, and performing an inspection.",
    category: "getting-started",
    keywords: ["overview", "portal", "start", "inspection", "help"],
    related: ["inspection-queue", "perform-inspection", "inspection-documents"],
    sections: [
      {
        heading: "What this portal is for",
        blocks: [
          {
            type: "p",
            text: "The inspection portal is where inspectors open assigned properties, perform inspections, and attach documents and photos. It uses its own sign-in so you stay in the inspection workflow.",
          },
        ],
      },
      {
        heading: "Finding help",
        blocks: [
          {
            type: "p",
            text: "Use the Help icon in the header, or Help in your account menu, to open this Help Center. Help stays inside the inspection portal so you remain signed in with your inspection login.",
          },
        ],
      },
    ],
  },
  {
    id: "inspection-queue",
    title: "Inspection queue",
    summary: "Find and open properties that need inspection.",
    category: "inspections",
    keywords: ["queue", "list", "assign", "pending", "open"],
    related: ["perform-inspection", "getting-started"],
    sections: [
      {
        heading: "Using the queue",
        blocks: [
          {
            type: "p",
            text: "The queue lists properties ready for inspection. Open a row to start or continue that inspection. Filters and search help you find a specific address or case.",
          },
        ],
      },
    ],
  },
  {
    id: "perform-inspection",
    title: "Perform an inspection",
    summary: "Complete inspection questions, outcomes, and submission.",
    category: "inspections",
    keywords: ["perform", "complete", "submit", "questions", "result"],
    related: ["inspection-documents", "inspection-queue"],
    sections: [
      {
        heading: "Steps",
        blocks: [
          {
            type: "ol",
            items: [
              "Open the property from the queue.",
              "Answer the inspection questions for that license type.",
              "Attach photos or documents when required.",
              "Submit when the inspection is complete.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "inspection-documents",
    title: "Documents and photos",
    summary: "Attach supporting files while performing an inspection.",
    category: "inspections",
    keywords: ["photos", "documents", "files", "attachments", "upload"],
    related: ["perform-inspection", "inspection-queue"],
    sections: [
      {
        heading: "Attachments",
        blocks: [
          {
            type: "p",
            text: "Upload photos and documents from the inspection screen when the jurisdiction requires evidence. Clear filenames and readable photos help reviewers confirm the outcome.",
          },
        ],
      },
    ],
  },
];

const ARTICLES_BY_ID = new Map(
  INSPECTION_HELP_ARTICLES.map((article) => [article.id, article]),
);

export function getInspectionHelpArticle(id: string): HelpArticle | undefined {
  return ARTICLES_BY_ID.get(id);
}

export function isInspectionHelpArticleId(
  id: string,
): id is InspectionHelpArticleId {
  return ARTICLES_BY_ID.has(id);
}

export function inspectionArticlesInCategory(
  categoryId: HelpCategory["id"],
): HelpArticle[] {
  return INSPECTION_HELP_ARTICLES.filter(
    (article) => article.category === categoryId,
  );
}
